import { App, Form, Spin } from "antd";
import Search from "antd/es/input/Search";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiError, getPointInventoryGetThunk } from "tm-shared/api";
import {
    Point,
    Product,
    SortOrder,
    TaskRejectProducts,
    TaskRoute,
    TaskUpdateProductBalances,
} from "tm-shared/types";

import { ROUTES } from "@/constants";
import { useCatalog } from "@/domains/product";
import { SVScanButton } from "@/features/SupervisorScan";
import { useAsyncAction, useInfiniteScroll, useQueryFilter } from "@/hooks";
import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
    usePointInventorySelector,
    useTaskPayloadSelector,
} from "@/hooks/redux";
import {
    makeCommand,
    useMakeCommand,
} from "@/hooks/requestQueue/useRequestQueue";
import { ConfirmActionBtn } from "@/pages/WorkPlan/Tasks/components/ConfirmActionBtn";
import {
    StyledContentContainer,
    StyledSearchWrapper,
} from "@/pages/WorkPlan/Tasks/shared";
import { getFilterCount } from "@/pages/WorkPlan/Tasks/utils";
import { Loading } from "@/shared/feedback/Loading";
import { Header } from "@/shared/layout";
import { HeaderFilterButton } from "@/shared/navigation/HeaderFilterButton";
import { Meta } from "@/shared/seo";
import {
    enqueueOrReplaceRequestQueueItem,
    enqueueRequestQueueItem,
    setPointInventories,
} from "@/store/slices/offline";
import {
    clearTaskPayload,
    setUpdateProductBalancesInventory,
} from "@/store/slices/progress";
import {
    patchProductsSelectedFilters,
    setProductsHideZero,
    setProductsShowOnlyMML,
    setProductsSortOrder,
    setProductsSortTag,
    setUpdateProductBalancesQuery,
} from "@/store/slices/ui";
import { setTaskCompleted } from "@/store/slices/work";

import {
    OfflineFilterDrawer,
    useOfflineFilteredProducts,
} from "../components/OfflineFilterDrawer";
import { InventoryProductCard } from "./components/InventoryProductCard";

interface InventoryProductsPageProps {
    point: Point;
    task: TaskUpdateProductBalances | TaskRejectProducts;
    isRejectTask: boolean;
}

export const InventoryProductsPage: FC<InventoryProductsPageProps> = ({
    point,
    task,
    isRejectTask,
}) => {
    // --- App hooks ---
    const { notification } = App.useApp();

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    // --- Derived identifiers ---

    const taskId = task.id;
    const pointId = point.id;

    // --- Store selectors ---

    const selectedFilters = useAppSelector(
        (s) => s.ui.productsFilter.selectedFilters,
    );
    const searchQuery = useAppSelector((s) => s.ui.updateProductBalances.query);

    const offlineMode = useOfflineModeSelector();
    const currentFormValues = useTaskPayloadSelector(
        TaskRoute.UpdateProductBalances,
        taskId,
    );
    const currentPointInventory = usePointInventorySelector(pointId);

    const { products } = useCatalog();

    // --- Local state ---

    const [open, setOpen] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false);

    // --- Memoized values ---

    const filterCount = useMemo(
        () => getFilterCount(selectedFilters),
        [selectedFilters],
    );

    // --- UI handlers ---

    const showDrawer = useCallback(() => setOpen(true), []);

    const onSearch = useCallback(
        (query: string) => dispatch(setUpdateProductBalancesQuery(query)),
        [dispatch],
    );

    // --- Task actions ---

    const { makeTaskCompleteCommands } = useMakeCommand();

    const [handleConfirmTask, sendLoading] = useAsyncAction(async () => {
        const restProducts = currentFormValues ?? {};

        if (isRejectTask) {
            dispatch(
                enqueueOrReplaceRequestQueueItem({
                    id: `${taskId}-reject`,
                    metadata: { pointId, title: "Актуалізація залишків" },
                    commands: [
                        makeCommand({
                            type: "patchPointInventorySet",
                            title: "Оновлення залишків на у базі",
                            params: { pointId, restProducts },
                        }),
                        makeCommand({
                            type: "getPointInventoryGetThunk",
                            title: "Синхронізація залишків",
                            params: { pointId },
                        }),
                    ],
                }),
            );

            dispatch(setPointInventories({ pointId, rest: restProducts }));
            navigate(-1);
        } else {
            const taskCompleteCommands = await makeTaskCompleteCommands({
                params: {
                    taskId,
                    hasPhoto: false,
                    payload: { restProducts },
                },
            });

            dispatch(
                enqueueRequestQueueItem({
                    id: taskId,
                    metadata: { pointId, taskId },
                    commands: [
                        makeCommand({
                            type: "patchPointInventorySet",
                            title: "Оновлення залишків на у базі",
                            params: { pointId, restProducts },
                        }),

                        ...taskCompleteCommands,

                        makeCommand({
                            type: "getPointInventoryGetThunk",
                            title: "Синхронізація залишків",
                            params: { pointId },
                        }),
                    ],
                }),
            );

            dispatch(clearTaskPayload({ taskId }));
            dispatch(setTaskCompleted({ targetId: pointId, taskId }));
            dispatch(setPointInventories({ pointId, rest: restProducts }));
            navigate(ROUTES.WorkPlan, { replace: true });
        }
    });

    // --- Effects ---

    useEffect(() => {
        if (!currentFormValues) {
            dispatch(
                setUpdateProductBalancesInventory({
                    taskId,
                    remains: currentPointInventory ?? {},
                }),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const fetchInitial = async () => {
            try {
                setInitialLoading(true);
                const { result } = await dispatch(
                    getPointInventoryGetThunk({ pointId }),
                ).unwrap();

                dispatch(
                    setUpdateProductBalancesInventory({
                        taskId,
                        remains: result.rest,
                    }),
                );
            } catch (error) {
                const { displayMessage, code } = getApiError(error);
                if (code !== "-32404") {
                    notification.error({
                        title: "Не вдалося завантажити залишки",
                        description: displayMessage,
                    });
                }
            } finally {
                setInitialLoading(false);
            }
        };

        if (!offlineMode) {
            fetchInitial();
        }

        dispatch(patchProductsSelectedFilters({}));
        dispatch(setProductsSortOrder(SortOrder.Asc));
        dispatch(setProductsSortTag(null));
        dispatch(setProductsHideZero(false));
        dispatch(setProductsShowOnlyMML(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- Filtered data ---

    const { sortedProducts } = useOfflineFilteredProducts({
        products,
        inventoryList: currentFormValues,
    });

    const searchFilteredProducts = useQueryFilter<Product>(
        sortedProducts,
        searchQuery,
        [
            (p) => p?.name,
            (p) => p?.barcode,
            (p) => p?.params?.description?.value,
            (p) => p?.params?.capacity?.value,
        ],
    );

    const { visibleItems, loadMore } = useInfiniteScroll<Product>(
        searchFilteredProducts,
    );
    const lastIndex = visibleItems.length - 1;

    // --- Render ---

    return (
        <>
            <Meta title="Додати товар" />
            <Header
                title="Додати товар"
                back
                rightActions={
                    <>
                        {!isRejectTask && (
                            <SVScanButton pointId={pointId} taskId={taskId} />
                        )}

                        <HeaderFilterButton
                            onClick={showDrawer}
                            filterCount={filterCount}
                        />
                    </>
                }
            />

            <StyledSearchWrapper>
                <Search
                    placeholder="Пошук"
                    defaultValue={searchQuery}
                    onSearch={onSearch}
                    allowClear
                    size="large"
                />
            </StyledSearchWrapper>

            <StyledContentContainer>
                {initialLoading ? (
                    <Spin size="large" />
                ) : (
                    <>
                        <Form>
                            {visibleItems.map((item, i) => {
                                const { barcode, name } = item;
                                const remainingCounter =
                                    currentFormValues?.[barcode];

                                return (
                                    <InventoryProductCard
                                        key={barcode}
                                        barcode={barcode}
                                        name={name}
                                        taskId={taskId}
                                        remainingCounter={remainingCounter}
                                        isLast={i === lastIndex}
                                        onView={loadMore}
                                    />
                                );
                            })}
                        </Form>

                        <ConfirmActionBtn
                            onConfirm={handleConfirmTask}
                            title="Завершити задачу"
                            description="Ви впевнені що хочете завершити виконання задачі?"
                            loading={sendLoading}
                        />
                    </>
                )}
            </StyledContentContainer>

            <OfflineFilterDrawer
                open={open}
                setOpen={setOpen}
                inventoryList={currentFormValues}
                products={products}
            />
            {sendLoading && <Loading />}
        </>
    );
};
